import { isWithinTimeRange } from '@chewy/loyalty-common/utils';
import React, { createContext, ReactNode, useContext, useMemo } from 'react';

import { useFeatureValue } from '@/hooks/useFeatureValue';
import { FeatureConfigKeys } from '@/utils/athena-feature-config';

export const LoyaltyThemeContext = createContext<{
  shouldUseHoliday2025Theme: boolean;
  shouldUseUpdatedTheme: boolean;
}>({ shouldUseHoliday2025Theme: false, shouldUseUpdatedTheme: false });

export const useLoyaltyTheme = () => useContext(LoyaltyThemeContext);

interface LoyaltyThemeProviderProps {
  timeZone?: string;
  children: ReactNode;
}

export const LoyaltyThemeProvider = ({ timeZone, children }: LoyaltyThemeProviderProps) => {
  const isHoliday2025BorderEnabled = useFeatureValue(
    FeatureConfigKeys.HOLIDAY_2025_BORDER_ENABLED,
    false,
  );
  const isUpdatedThemeEnabled = useFeatureValue(FeatureConfigKeys.UPDATED_THEME_ENABLED, false);
  const updatedThemeConfig = useFeatureValue(FeatureConfigKeys.UPDATED_THEME_CONFIG, undefined);

  const isWithinValidTimeRange = useMemo(() => {
    if (!updatedThemeConfig?.startTimestamp || !updatedThemeConfig?.endTimestamp) {
      return false;
    }

    return isWithinTimeRange(
      updatedThemeConfig.startTimestamp,
      updatedThemeConfig.endTimestamp,
      timeZone,
    );
  }, [updatedThemeConfig, timeZone]);

  const shouldUseUpdatedTheme = isUpdatedThemeEnabled && isWithinValidTimeRange;

  const shouldUseHoliday2025Theme = isHoliday2025BorderEnabled && shouldUseUpdatedTheme;

  return (
    <LoyaltyThemeContext.Provider value={{ shouldUseHoliday2025Theme, shouldUseUpdatedTheme }}>
      {children}
    </LoyaltyThemeContext.Provider>
  );
};
